import React, { useState, useEffect } from 'react';
import { styled } from '@compiled/react';
import { LoadingButton } from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useActiveIncidents } from '@atlassian/jira-servicedesk-linked-object-incidents-store/src/controllers/index.tsx';
import { ObjectActiveIncidentsPanelList } from '@atlassian/jira-servicedesk-object-active-incidents-panel/src/ui/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const ActiveIncidentsIndicator = ({ services }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const activeIncidents = useActiveIncidents();
	const buttonLabel = formatMessage(messages.viewActiveIncidents);

	const servicesToDisplay = services.filter(
		({ id: serviceId }) =>
			activeIncidents[serviceId]?.error || activeIncidents[serviceId]?.data?.activeIncidents.length,
	);
	const loading = services.some(({ id: serviceId }) => activeIncidents[serviceId]?.loading);
	const serviceWithError = services.find(({ id: serviceId }) => activeIncidents[serviceId]?.error);
	const errorType = serviceWithError ? activeIncidents[serviceWithError.id]?.error : null;

	useEffect(() => {
		if (loading) {
			return;
		}

		if (errorType) {
			fireErrorAnalytics({
				meta: {
					id: 'activeIncidentsIndicator',
					packageName: 'jiraIssueFieldServiceEntity',
				},
				attributes: {
					errorType,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		} else {
			fireTrackAnalytics(createAnalyticsEvent({}), 'activeIncidentsIndicator rendered', {
				numServices: servicesToDisplay.length,
			});
		}
	}, [loading, errorType, servicesToDisplay.length, createAnalyticsEvent]);

	if (servicesToDisplay.length === 0 && !loading && !errorType) {
		// We want to force show the indicator in the error/loading state.
		return null;
	}

	return (
		<Tooltip content={buttonLabel}>
			<InlineDialog
				onClose={() => setIsDialogOpen(false)}
				content={
					<InlineDialogContentWrapper>
						<ObjectActiveIncidentsPanelList
							services={servicesToDisplay.map(({ id, name }) => ({ id, name }))}
						/>
					</InlineDialogContentWrapper>
				}
				isOpen={isDialogOpen}
				placement="bottom-end"
				messageId="issue-field-service-entity.common.ui.active-incidents-indicator.inline-dialog"
				messageType="transactional"
				testId="issue-field-service-entity.common.ui.active-incidents-indicator.incident-dialog"
			>
				<LoadingButton
					appearance="subtle"
					isSelected={isDialogOpen}
					onClick={() => setIsDialogOpen(true)}
					iconBefore={
						errorType ? (
							<ErrorIcon
								spacing="spacious"
								label={buttonLabel}
								color={token('color.icon.danger')}
								testId="issue-field-service-entity.common.ui.active-incidents-indicator.error-icon"
							/>
						) : (
							<WarningIcon
								spacing="spacious"
								label={buttonLabel}
								color={token('color.icon.warning')}
								testId="issue-field-service-entity.common.ui.active-incidents-indicator.incident-warning"
							/>
						)
					}
					isLoading={loading}
					testId="issue-field-service-entity.common.ui.active-incidents-indicator.loading-button"
					interactionName="auto-generated.issue-field-service-entity.common.ui.active-incidents-indicator.loading-button.button"
				/>
			</InlineDialog>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineDialogContentWrapper = styled.div({
	marginTop: token('space.negative.200'),
	marginRight: token('space.negative.300'),
	marginBottom: token('space.negative.200'),
	marginLeft: token('space.negative.300'),
	maxHeight: '300px',
	overflow: 'auto',
});
